// v10/src/components/WeatherInfo.js — rediseño mobile-first sin grid fijo
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, MapPin } from 'lucide-react';

const OPENWEATHER_API_KEY = process.env.REACT_APP_OPENWEATHER_KEY;
const DEFAULT_LOCATION = { lat: -42.7692, lon: -65.0386 };

const Windsock = ({ speedKts = 0, darkMode }) => {
  const fill = Math.min(1, speedKts / 20);
  const bodyLen = 18 + fill * 20;
  const tipH = 10 - fill * 6;
  return (
    <svg viewBox="0 0 56 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width:'44px', height:'28px' }}>
      <line x1="10" y1="2" x2="10" y2="30" stroke={darkMode ? '#6b7280' : '#9ca3af'} strokeWidth="2.5" strokeLinecap="round" />
      <line x1="10" y1="8" x2="16" y2="8" stroke={darkMode ? '#6b7280' : '#9ca3af'} strokeWidth="2" strokeLinecap="round" />
      <circle cx="10" cy="2" r="2.5" fill={darkMode ? '#9ca3af' : '#6b7280'} />
      <path d={`M16,5 L${16 + bodyLen * 0.33},5 L${16 + bodyLen * 0.33},13 L16,14 Z`} fill="#f97316" />
      <path d={`M${16 + bodyLen * 0.33},5 L${16 + bodyLen * 0.66},5.5 L${16 + bodyLen * 0.66},12.5 L${16 + bodyLen * 0.33},13 Z`} fill="white" opacity="0.95" />
      <path d={`M${16 + bodyLen * 0.66},5.5 L${16 + bodyLen},9 L${16 + bodyLen},${9 + tipH * 0.5} L${16 + bodyLen * 0.66},12.5 Z`} fill="#f97316" />
    </svg>
  );
};

const weatherIcon = (code) => {
  if (!code) return '🌤';
  if (code === 800) return '☀️';
  if (code > 800)  return '⛅';
  if (code >= 600) return '❄️';
  if (code >= 300) return '🌧';
  if (code >= 200) return '⛈';
  return '🌫';
};

const degToCardinal = (deg) => {
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SO', 'O', 'NO'];
  return dirs[Math.round(((deg % 360) / 45)) % 8];
};

const WeatherInfo = ({ darkMode, onWeatherUpdate }) => {
  const [weather, setWeather]           = useState(null);
  const [loading, setLoading]           = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [location, setLocation]         = useState(DEFAULT_LOCATION);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => setLocation({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
        ()  => fetchWeather(DEFAULT_LOCATION)
      );
    } else {
      fetchWeather(DEFAULT_LOCATION);
    }
  }, []);

  useEffect(() => { if (location) fetchWeather(location); }, [location]);

  const fetchWeather = async (coords) => {
    setLoading(true);
    try {
      const res  = await fetch(`https://api.openweathermap.org/data/2.5/weather?lat=${coords.lat}&lon=${coords.lon}&appid=${OPENWEATHER_API_KEY}&units=metric&lang=es`);
      const data = await res.json();
      const payload = {
        location:         data.name,
        temperature:      Math.round(data.main.temp),
        condition:        data.weather[0].description,
        qnh:              Math.round(data.main.pressure),
        windSpeedKts:     Math.round(data.wind.speed * 1.94384),
        windDirectionDeg: data.wind.deg || 0,
        weatherCode:      data.weather[0].id,
      };
      setWeather(payload);
      if (onWeatherUpdate) onWeatherUpdate(payload);
    } catch {
      const fallback = { location: 'Puerto Madryn', temperature: 20, condition: 'despejado', qnh: 1013, windSpeedKts: 12, windDirectionDeg: 248, weatherCode: 800 };
      setWeather(fallback);
      if (onWeatherUpdate) onWeatherUpdate(fallback);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleRefresh = () => { setIsRefreshing(true); fetchWeather(location); };

  const wrap = `rounded-2xl border mb-4 overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100 shadow-sm'}`;

  if (loading) return (
    <div className={`${wrap} p-4 flex items-center gap-3`}>
      <RefreshCw className="w-4 h-4 animate-spin text-blue-500" />
      <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Cargando clima...</span>
    </div>
  );
  if (!weather) return null;

  const stats = [
    {
      icon: <span style={{ fontSize: '22px', lineHeight: 1 }}>{weatherIcon(weather.weatherCode)}</span>,
      value: `${weather.temperature}°`,
      label: weather.condition,
      labelShort: weather.condition.split(' ').slice(0, 2).join(' '),
      color: darkMode ? '#93c5fd' : '#2563eb',
      bg:    darkMode ? 'rgba(37,99,235,0.12)' : 'rgba(219,234,254,0.6)',
    },
    {
      icon: <span style={{ fontSize: '18px', color: darkMode ? '#4ade80' : '#16a34a', fontWeight: 800 }}>hPa</span>,
      value: String(weather.qnh),
      label: 'QNH',
      labelShort: 'QNH',
      color: darkMode ? '#4ade80' : '#16a34a',
      bg:    darkMode ? 'rgba(22,163,74,0.12)' : 'rgba(220,252,231,0.6)',
    },
    {
      icon: <Windsock speedKts={weather.windSpeedKts} darkMode={darkMode} />,
      value: `${weather.windSpeedKts} kt`,
      label: `${degToCardinal(weather.windDirectionDeg)} ${weather.windDirectionDeg}°`,
      labelShort: `${degToCardinal(weather.windDirectionDeg)} ${weather.windDirectionDeg}°`,
      color: darkMode ? '#fb923c' : '#ea580c',
      bg:    darkMode ? 'rgba(234,88,12,0.12)' : 'rgba(254,243,199,0.6)',
    },
  ];

  return (
    <motion.div className={wrap} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px 8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <MapPin style={{ width: '13px', height: '13px', color: darkMode ? '#6b7280' : '#9ca3af' }} />
          <span style={{ fontSize: '12px', fontWeight: 600, color: darkMode ? '#9ca3af' : '#6b7280' }}>{weather.location}</span>
        </div>
        <motion.button onClick={handleRefresh} disabled={isRefreshing}
          style={{ padding: '4px', borderRadius: '8px', border: 'none', background: 'transparent', cursor: 'pointer', color: darkMode ? '#6b7280' : '#9ca3af', display: 'flex' }}
          whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
          <RefreshCw style={{ width: '13px', height: '13px' }} className={isRefreshing ? 'animate-spin' : ''} />
        </motion.button>
      </div>

      {/* Cards flotantes — 3 columnas iguales sin altura fija */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', padding: '0 10px 12px' }}>
        {stats.map((s, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}
            style={{ borderRadius: '14px', background: s.bg, padding: '10px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}
          >
            {/* Ícono */}
            <div style={{ height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {s.icon}
            </div>
            {/* Número grande */}
            <p style={{ margin: 0, fontSize: '18px', fontWeight: 900, color: darkMode ? 'white' : '#0f172a', lineHeight: 1, textAlign: 'center', whiteSpace: 'nowrap' }}>
              {s.value}
            </p>
            {/* Label */}
            <p style={{ margin: 0, fontSize: '10px', fontWeight: 600, color: s.color, textAlign: 'center', lineHeight: 1.2, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {s.labelShort}
            </p>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

export default WeatherInfo;
